import axios, { type InternalAxiosRequestConfig, type AxiosResponse, AxiosError } from 'axios';

export const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://192.168.1.4:8000/api/';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15000, // évite les attentes infinies côté UI
});

function getTokens() {
  try {
    return {
      access: localStorage.getItem('access'),
      refresh: localStorage.getItem('refresh'),
    };
  } catch {
    return { access: null, refresh: null };
  }
}

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // Si on envoie du FormData, laisser le navigateur définir le Content-Type + boundary
  if (config.data instanceof FormData && config.headers) {
    delete (config.headers as any)['Content-Type'];
  }
  const { access } = getTokens();
  if (access && config.headers) {
    (config.headers as any).Authorization = `Bearer ${access}`;
  }
  return config;
});


/** Une erreur sans réponse HTTP = serveur injoignable (réseau, CORS, timeout). */
export function estErreurReseau(e: any): boolean {
  return Boolean(e) && !e.response;
}

/**
 * Rafraîchit le jeton d'accès.
 *
 * Instance nue, sans intercepteur : passer par `api` ferait qu'un
 * rafraîchissement refusé déclenche son propre intercepteur et se met en file
 * derrière lui-même — l'application se fige alors sur des écrans vides.
 */
export async function rafraichirJeton(refresh: string): Promise<string> {
  const { data } = await axios.post<{ access: string; refresh?: string }>(
    `${API_BASE_URL}users/token/refresh/`,
    { refresh },
    { headers: { 'Content-Type': 'application/json' }, timeout: 15000 },
  );
  localStorage.setItem('access', data.access);
  // Rotation activée côté serveur : sans conserver le nouveau jeton, le
  // rafraîchissement suivant échoue et déconnecte l'utilisateur.
  if (data.refresh) localStorage.setItem('refresh', data.refresh);
  return data.access;
}

let isRefreshing = false;
let failedQueue: Array<{ resolve: (value?: unknown) => void; reject: (reason?: any) => void; config: any }> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((p) => {
    if (error) {
      p.reject(error);
    } else {
      if (p.config.headers && token) {
        p.config.headers.Authorization = `Bearer ${token}`;
      }
      p.resolve(api.request(p.config));
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest: any = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject, config: originalRequest });
        });
      }
      originalRequest._retry = true;
      isRefreshing = true;
      const { refresh: refreshUtilise } = getTokens();
      try {
        if (!refreshUtilise) throw new Error('Missing refresh token');
        const nouvelAcces = await rafraichirJeton(refreshUtilise);
        processQueue(null, nouvelAcces);
        isRefreshing = false;
        if (originalRequest.headers) originalRequest.headers.Authorization = `Bearer ${nouvelAcces}`;
        return api.request(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;

        // Serveur injoignable : la session est peut-être toujours valide.
        // L'effacer déconnecterait pour une simple coupure réseau.
        if (estErreurReseau(refreshError)) {
          processQueue(refreshError as any, null);
          return Promise.reject(refreshError);
        }

        // La rotation des jetons bannit l'ancien dès qu'il est utilisé. Avec
        // plusieurs onglets, le second se voit refuser un jeton qu'un autre
        // vient de renouveler. Si le stockage a changé entre-temps, c'est ce
        // cas : on repart avec le jeton renouvelé au lieu de déconnecter.
        const actuels = getTokens();
        if (actuels.access && actuels.refresh && actuels.refresh !== refreshUtilise) {
          processQueue(null, actuels.access);
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${actuels.access}`;
          }
          return api.request(originalRequest);
        }

        processQueue(refreshError as any, null);
        localStorage.removeItem('access');
        localStorage.removeItem('refresh');
        localStorage.removeItem('user');
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Message lisible pour une erreur d'appel API.
 *
 * Le cas important est la session expirée : sans distinction, l'interface
 * affichait des compteurs à zéro, qu'un utilisateur pouvait prendre pour une
 * perte de données.
 */
export function messageErreurApi(e: any, defaut = 'Erreur de chargement'): string {
  const statut = e?.response?.status;
  if (statut === 401) return 'Session expirée — reconnectez-vous pour voir les données.';
  if (statut === 403) return "Vous n'avez pas accès à ces données.";
  if (e?.code === 'ECONNABORTED' || e?.message === 'Network Error') {
    return 'Serveur injoignable — vérifiez votre connexion.';
  }
  return e?.response?.data?.detail || e?.message || defaut;
}

export default api;
